import http from './httpClient';

/**
 * @typedef {Object} ProductionPlan
 * @property {string} id
 * @property {string} name
 * @property {string} status
 * @property {string} scheduledDate
 * @property {Array<{productId: string, quantity: number}>} items
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} ProductionRun
 * @property {string} id
 * @property {string} planId
 * @property {string} status
 * @property {string} startTime
 * @property {string} endTime
 * @property {Array<{productId: string, quantity: number}>} items
 * @property {string} updatedAt
 */

const productionApi = {
  // Plans
  async listPlans(params = {}) {
    const { data } = await http.get('/api/v1/production/plans', { params });
    return data;
  },

  async getPlan(id) {
    const { data } = await http.get(`/api/v1/production/plans/${id}`);
    return data;
  },

  async createPlan(payload) {
    const { data } = await http.post('/api/v1/production/plans', payload);
    return data;
  },

  async updatePlan(id, payload) {
    const { data } = await http.put(`/api/v1/production/plans/${id}`, payload);
    return data;
  },

  async removePlan(id) {
    await http.delete(`/api/v1/production/plans/${id}`);
  },

  async recalcPlan(id) {
    const { data } = await http.post(`/api/v1/production/plans/${id}/recalc`);
    return data;
  },

  async planRequirements(id) {
    const { data } = await http.get(`/api/v1/production/plans/${id}/requirements`);
    return data;
  },

  async capacityCheck(id) {
    const { data } = await http.get(`/api/v1/production/plans/${id}/capacity-check`);
    return data;
  },

  // Runs
  async listRuns(params = {}) {
    const { data } = await http.get('/api/v1/production/runs', { params });
    return data;
  },

  async getRun(id) {
    const { data } = await http.get(`/api/v1/production/runs/${id}`);
    return data;
  },

  async createRun(payload) {
    const { data } = await http.post('/api/v1/production/runs', payload);
    return data;
  },

  async startRun(id, payload) {
    const { data } = await http.put(`/api/v1/production/runs/${id}/start`, payload);
    return data;
  },

  async completeRun(id, payload) {
    const { data } = await http.put(`/api/v1/production/runs/${id}/complete`, payload);
    return data;
  },

  async runRequirements(id) {
    const { data } = await http.get(`/api/v1/production/runs/${id}/requirements`);
    return data;
  },

  async getRecipe(id) {
    const { data } = await http.get(`/api/v1/production/runs/${id}/recipe`);
    return data;
  },

  async getFormats() {
    const { data } = await http.get('/api/v1/production/formats');
    return data;
  },

  async getStageData(id, stage = 'brew') {
    try {
      const { data } = await http.get(`/api/v1/production/runs/${id}/stage-data`, { params: { stage } });
      return data;
    } catch (e) {
      const { data } = await http.get(`/api/v1/production/runs/${id}`);
      return data;
    }
  },

  async saveStageData(id, payload, stage = 'brew') {
    try {
      const { data } = await http.put(`/api/v1/production/runs/${id}/stage-data/${stage}`, payload);
      return data;
    } catch (err) {
      try {
        const { data } = await http.put(`/api/v1/production/runs/${id}/stage-data`, payload);
        return data;
      } catch (e) {
        const { data } = await http.put(`/api/v1/production/runs/${id}`, payload);
        return data;
      }
    }
  },

  async listFinishedGoods(params = {}) {
    const { data } = await http.get('/api/v1/production/finished-goods', { params });
    return data;
  },
};

export default productionApi;